import { flattenTransfers, type FlattenedTransferRow } from './flattenTransfers';
import {
  buildRequestingDirectorSignerJson,
  buildTransferorSignerJson,
  buildTransfereeSignerJson,
} from './transferSigners';

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

function pickValue(source: Record<string, any>, keys: string[]): string {
  for (const key of keys) {
    const parts = key.split('.');
    let cur: any = source;
    for (const p of parts) cur = cur?.[p];
    const v = trim(cur?.value ?? cur);
    if (v) return v;
  }
  return '';
}

function resolveCompanyAddress(formData: Record<string, any>): string {
  return pickValue(formData, [
    'companyAddress',
    'registeredOfficeAddress',
    'registered_office_address',
    'newOfficeAddress',
  ]);
}

function resolveDocumentDate(formData: Record<string, any>): string {
  return trim(formData.transactionDate);
}

function baseCompanyVars(formData: Record<string, any>) {
  return {
    company_name: pickValue(formData, ['entityName', 'companyName', 'entityName.value']),
    uen: pickValue(formData, ['entityRegNo', 'companyRegistrationNo', 'entityRegNo.value']),
    registered_office_address: resolveCompanyAddress(formData),
  };
}

function baseTransferVars(row: FlattenedTransferRow, formData: Record<string, any>) {
  return {
    ...baseCompanyVars(formData),
    transferor_name: row.transferorName,
    transferee_name: row.transfereeName,
    share_class: row.shareClassName || row.shareClassCode,
    shares_numeral: row.sharesNumeral,
    shares_words: row.sharesWords,
    consideration_numeral: row.considerationNumeral,
    consideration_words: row.considerationWords,
    document_date: resolveDocumentDate(formData),
  };
}

export function buildTransferTaskFormData(
  formData: Record<string, any>,
  entityCd: string,
): Record<string, any> {
  const out = { ...(formData || {}) };
  const ec = trim(entityCd);
  if (ec) {
    out.entity = out.entity || ec;
    out.entityCd = out.entityCd || ec;
  }
  return out;
}

export function buildTransferEfilingSeedPayload(input: {
  eFilingDataValidation: Record<string, any> | null | undefined;
  eFilingDataForApi: Record<string, any> | null | undefined;
}) {
  return {
    eFilingDataValidation: input.eFilingDataValidation || null,
    eFilingDataForApi: input.eFilingDataForApi || null,
  };
}

export function toDirectorLetterVariables(input: {
  formData: Record<string, any>;
  row: FlattenedTransferRow;
}): Record<string, any> {
  const { formData, row } = input;
  const signers = buildRequestingDirectorSignerJson({
    directorName: trim(formData.requestingDirectorName),
    directorEmail: trim(formData.requestingDirectorEmail),
  });
  return {
    ...baseTransferVars(row, formData),
    director_name: trim(formData.requestingDirectorName),
    director_id: trim(formData.requestingDirectorId),
    signers: signers,
    Signers: signers,
  };
}

export function toDirectorsResolutionVariables(input: {
  formData: Record<string, any>;
  row: FlattenedTransferRow;
  directorsSignersJson: string;
}): Record<string, any> {
  return {
    ...baseTransferVars(input.row, input.formData),
    signers: input.directorsSignersJson,
    Signers: input.directorsSignersJson,
  };
}

export function toMembersResolutionVariables(input: {
  formData: Record<string, any>;
  row: FlattenedTransferRow;
  membersSignersJson: string;
}): Record<string, any> {
  return {
    ...baseTransferVars(input.row, input.formData),
    signers: input.membersSignersJson,
    Signers: input.membersSignersJson,
  };
}

export function toShareTransferFormVariables(input: {
  formData: Record<string, any>;
  row: FlattenedTransferRow;
}): Record<string, any> {
  const { row } = input;
  const isTransfereeCorporate = row.transfereeCategoryCd === 'B09001';
  const isTransfereeIndividual = !!row.transfereeCategoryCd && !isTransfereeCorporate;
  const transferorSigners = buildTransferorSignerJson({
    categoryCd: row.transferorCategoryCd,
    transferorName: row.transferorName,
    representativeName: row.transferorRepresentativeName,
  });
  const transfereeSigners = buildTransfereeSignerJson({
    categoryCd: row.transfereeCategoryCd,
    transfereeName: row.transfereeName,
    representativeName: row.transfereeRepresentativeName,
  });
  return {
    ...baseTransferVars(row, input.formData),
    transferee_is_individual: isTransfereeIndividual,
    transferee_is_corporate: isTransfereeCorporate,
    transferor_has_witness: row.transferorHasWitness,
    TransferorWitness: row.transferorWitnessSignersJson,
    transferee_has_witness: row.transfereeHasWitness,
    TransfereeWitness: row.transfereeWitnessSignersJson,
    signers: transferorSigners,
    Signers: transferorSigners,
    TransfereeSigners: transfereeSigners,
    transferee_signers: transfereeSigners,
  };
}

export function buildTransferRows(input: {
  formData: Record<string, any>;
}): FlattenedTransferRow[] {
  return flattenTransfers({ formData: input.formData || {} });
}

export function rowPersonLabel(row: FlattenedTransferRow): string {
  return `${row.transferorName} → ${row.transfereeName}`;
}
